import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import {
	MantineReactTable,
	MRT_ColumnDef,
	MRT_PaginationState,
} from 'mantine-react-table'
import { color } from 'src/variable/constant'
import { Flex, Pagination, Stack } from '@mantine/core'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import { useSearchParams } from 'react-router-dom'

import {
	IconCheck,
	IconDotsVertical,
	IconEdit,
	IconEye,
	IconInfoCircle,
	IconTrash,
} from '@tabler/icons-react'
import classes from './TableComponent.module.scss'

const DEFAULT_PRESERVE_QUERY_KEYS = ['tab']

interface TableComponentProps<TData extends object> {
	total: number
	data: TData[]
	handleChangePage: (value: number) => void
	handleChangePageSize?: (value: number) => void
	handleEdit?: (row: any) => void
	handleDetail?: (row: any) => void
	handleDelete?: (row: any) => void
	detailActionLabel?: string
	editActionLabel?: string
	useEditIconForDetail?: boolean
	detailActionIcon?: 'info' | 'edit' | 'eye' | 'check' | 'review'
	autoExpandAll?: boolean
	hidePagination?: boolean
	hideMoreActions?: boolean
	disableActionTooltips?: boolean
	/**
	 * Skips Mantine React Table's row/column model for small, read-mostly lists.
	 * This keeps navigation responsive on pages with many custom cells/actions.
	 */
	lean?: boolean
	compact?: boolean
	lockActionColumn?: boolean
	pagination: MRT_PaginationState
	loading: boolean
	columns: MRT_ColumnDef<TData>[]
	enableRowActions?: boolean
	enableExpanding?: boolean
	_renderCustomActions?: any
	tableStyle?: 'default' | 'book'
	hasSubTable?: (row: any) => boolean
	syncQueryUrl?: boolean
	pageQueryKey?: string
	pageSizeQueryKey?: string
	searchQueryKey?: string
	searchQueryValue?: string
	onSearchQueryInit?: (value: string) => void
	filterQueryValues?: Record<string, string | number | boolean | null | undefined>
	onFilterQueryInit?: (query: Record<string, string>) => void
	preserveQueryKeys?: string[]
	wrapperClassName?: string
	[key: string]: any
}

function TableComponent<TData extends object>({
	total,
	data,
	handleChangePage,
	handleChangePageSize,
	loading = false,
	pagination,
	columns,
	enableRowActions = true,
	enableExpanding,
	handleEdit,
	handleDetail,
	handleDelete,
	detailActionLabel = 'Detail',
	editActionLabel = 'Edit',
	useEditIconForDetail = false,
	detailActionIcon,
	autoExpandAll = false,
	hidePagination = false,
	hideMoreActions = false,
	disableActionTooltips = false,
	lean = false,
	compact = false,
	lockActionColumn = false,
	tableStyle = 'default',
	renderDetailPanel,
	_renderCustomActions,
	hasSubTable,
	syncQueryUrl,
	pageQueryKey = 'page',
	pageSizeQueryKey = 'limit',
	searchQueryKey = 'search',
	searchQueryValue,
	actionColumnSize = 132,
	onSearchQueryInit,
	filterQueryValues,
	onFilterQueryInit,
	preserveQueryKeys = DEFAULT_PRESERVE_QUERY_KEYS,
	wrapperClassName,
}: TableComponentProps<TData>) {
	const [urlSearchParams, setUrlSearchParams] = useSearchParams()
	const hasInitializedQuerySyncRef = useRef(false)
	const shouldSyncQueryUrl = syncQueryUrl ?? !hidePagination
	const serializedFilterQueryValues = JSON.stringify(filterQueryValues ?? {})
	const serializedPreserveQueryKeys = JSON.stringify(preserveQueryKeys)
	const resolvedFilterQueryValues = useMemo(
		() => filterQueryValues,
		// Use the serialized value so object-literal props do not retrigger URL sync.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[serializedFilterQueryValues],
	)
	const resolvedPreserveQueryKeys = useMemo(
		() => preserveQueryKeys,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[serializedPreserveQueryKeys],
	)

	const isBookStyle = tableStyle === 'book'
	const shouldEnableExpanding = enableExpanding ?? Boolean(renderDetailPanel)
	const actionColumnBackground = isBookStyle ? '#f7fcf8' : '#f9fbfa'
	const actionColumnBorder = isBookStyle ? '#cfe4d6' : '#d9e8de'
	const stickyActionStyles = {
		position: 'sticky' as const,
		right: 0,
		zIndex: 100,
		borderLeft: `1px solid ${actionColumnBorder}`,
		boxShadow:
			'-14px 0 24px -20px rgba(15, 23, 42, 0.55), inset 1px 0 0 rgba(255, 255, 255, 0.9)',
	}

	const resolvedDetailActionIcon =
		detailActionIcon ?? (useEditIconForDetail ? 'edit' : 'info')
	const detailActionButton = (row: any) => (
		<button
			type="button"
			onClick={() => handleDetail(row)}
			className={getActionButtonClassName('primary')}
			title={detailActionLabel}
			aria-label={detailActionLabel}
		>
			{resolvedDetailActionIcon === 'edit' ? (
				<IconEdit size={16} />
			) : resolvedDetailActionIcon === 'eye' ? (
				<IconEye size={16} />
			) : resolvedDetailActionIcon === 'check' ? (
				<IconCheck size={16} />
			) : resolvedDetailActionIcon === 'review' ? (
				<IconEye size={16} />
			) : (
				<IconInfoCircle size={16} />
			)}
		</button>
	)

	const getActionButtonClassName = (
		type: 'default' | 'primary' | 'muted' | 'danger' | 'disabled',
	) => `${classes.actionButton} ${classes[`${type}Action`]}`

	const rowHasSubTable = (row: any) => {
		if (typeof hasSubTable === 'function') {
			return hasSubTable(row)
		}

		const original = row?.original ?? {}
		const candidates = [
			original?.subtable,
			original?.subTable,
			original?.children,
			original?.chapters,
			original?.items,
		]

		return candidates.some((item) => Array.isArray(item) && item.length > 0)
	}

	const setUrlSearchParamsIfChanged = useCallback(
		(updater: (prev: URLSearchParams) => URLSearchParams) => {
			setUrlSearchParams((prev) => {
				const next = updater(prev)

				if (next.toString() === prev.toString()) {
					return prev
				}

				return next
			}, { replace: true })
		},
		[setUrlSearchParams],
	)

	useEffect(() => {
		if (!shouldSyncQueryUrl || hasInitializedQuerySyncRef.current) {
			return
		}

		const pageFromUrl = Number(urlSearchParams.get(pageQueryKey))
		if (
			Number.isInteger(pageFromUrl) &&
			pageFromUrl > 0 &&
			pageFromUrl !== pagination.pageIndex
		) {
			handleChangePage(pageFromUrl)
		}

		if (typeof handleChangePageSize === 'function') {
			const pageSizeFromUrl = Number(urlSearchParams.get(pageSizeQueryKey))
			if (
				Number.isInteger(pageSizeFromUrl) &&
				pageSizeFromUrl > 0 &&
				pageSizeFromUrl !== pagination.pageSize
			) {
				handleChangePageSize(pageSizeFromUrl)
			}
		}

		   if (typeof onSearchQueryInit === 'function') {
			   // Read from both search and search_key for compatibility
			   const searchValueFromQuery =
				   urlSearchParams.get(searchQueryKey) || urlSearchParams.get('search_key') || ''
			   onSearchQueryInit(searchValueFromQuery)
		   }

		if (typeof onFilterQueryInit === 'function') {
			const query: Record<string, string> = {}
			urlSearchParams.forEach((value, key) => {
				query[key] = value
			})
			onFilterQueryInit(query)
		}

		hasInitializedQuerySyncRef.current = true
	}, [
		handleChangePage,
		handleChangePageSize,
		onSearchQueryInit,
		onFilterQueryInit,
		pageQueryKey,
		pageSizeQueryKey,
		pagination.pageIndex,
		pagination.pageSize,
		searchQueryKey,
		shouldSyncQueryUrl,
		urlSearchParams,
	])

	useEffect(() => {
		if (!shouldSyncQueryUrl || !hasInitializedQuerySyncRef.current) {
			return
		}

		setUrlSearchParamsIfChanged((prev) => {
			const next = new URLSearchParams(prev)
			next.set(pageQueryKey, String(pagination.pageIndex))
			next.set(pageSizeQueryKey, String(pagination.pageSize))
			return next
		})
	}, [
		pageQueryKey,
		pageSizeQueryKey,
		pagination.pageIndex,
		pagination.pageSize,
		setUrlSearchParamsIfChanged,
		shouldSyncQueryUrl,
	])

	   useEffect(() => {
		   if (
			   !shouldSyncQueryUrl ||
			   typeof searchQueryValue === 'undefined' ||
			   !hasInitializedQuerySyncRef.current
		   ) {
			   return
		   }

		   setUrlSearchParamsIfChanged((prev) => {
			   const next = new URLSearchParams(prev)
			   const normalizedSearchValue = searchQueryValue.trim()

			   if (normalizedSearchValue) {
				   next.set(searchQueryKey, normalizedSearchValue)
				   next.set('search_key', normalizedSearchValue)
			   } else {
				   next.delete(searchQueryKey)
				   next.delete('search_key')
			   }

			   return next
		   })
	   }, [
		   searchQueryKey,
		   searchQueryValue,
		   setUrlSearchParamsIfChanged,
		   shouldSyncQueryUrl,
	   ])

	useEffect(() => {
		if (!shouldSyncQueryUrl || !hasInitializedQuerySyncRef.current) {
			return
		}

		const filterEntries = Object.entries(resolvedFilterQueryValues ?? {})

		setUrlSearchParamsIfChanged((prev) => {
			const next = new URLSearchParams(prev)
			const protectedQueryKeys = new Set([
				pageQueryKey,
				pageSizeQueryKey,
				searchQueryKey,
				'search_key',
				...resolvedPreserveQueryKeys,
			])

			for (const [key, value] of filterEntries) {
				if (protectedQueryKeys.has(key)) {
					continue
				}

				if (
					value === null ||
					typeof value === 'undefined' ||
					(typeof value === 'string' && value.trim() === '')
				) {
					next.delete(key)
					continue
				}

				next.set(key, String(value))
			}

			return next
		})
	}, [
		pageQueryKey,
		pageSizeQueryKey,
		resolvedFilterQueryValues,
		resolvedPreserveQueryKeys,
		searchQueryKey,
		serializedFilterQueryValues,
		serializedPreserveQueryKeys,
		setUrlSearchParamsIfChanged,
		shouldSyncQueryUrl,
	])

	const from = useMemo(
		() => pagination.pageSize * (pagination.pageIndex - 1) + 1,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(pagination)],
	)
	let to = useMemo(
		() => pagination.pageSize * (pagination.pageIndex - 1) + 10,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(pagination)],
	)

	if (to > total) {
		to = total
	}

	const tableWrapperClassName = [
		wrapperClassName,
		lockActionColumn ? classes.lockActionTable : '',
	].filter(Boolean).join(' ')
	const renderLeanCell = (column: any, row: any) => {
		const accessorKey = column.accessorKey as string | undefined
		const value = accessorKey
			? accessorKey.split('.').reduce((current, key) => current?.[key], row)
			: undefined

		if (typeof column.Cell === 'function') {
			return column.Cell({
				cell: { getValue: () => value },
				row: { original: row },
			})
		}

		return value ?? '-'
	}

	const renderLeanActions = (row: any) => (
		<Flex gap={8} align="center" wrap="nowrap">
			{_renderCustomActions?.({ original: row })}
			{handleDetail && detailActionButton({ original: row })}
			{handleEdit && (
				<button
					type="button"
					className={getActionButtonClassName('default')}
					onClick={() => handleEdit({ original: row })}
					title={editActionLabel}
					aria-label={editActionLabel}
				>
					<IconEdit size={16} />
				</button>
			)}
			{handleDelete && (
				row?.deleted_at ? (
					<button type="button" className={getActionButtonClassName('disabled')} disabled>
						<IconTrash size={16} />
					</button>
				) : (
					<button
						type="button"
						className={getActionButtonClassName('danger')}
						onClick={() => handleDelete({ original: row })}
						title="Delete"
						aria-label="Delete"
					>
						<IconTrash size={16} />
					</button>
				)
			)}
		</Flex>
	)

	return (
		<Stack sx={{ width: '100%' }} className={tableWrapperClassName || undefined}>
			{lean ? (
				<div
					data-testid="lean-table"
					style={{
						border: '1px solid #e6ebe7',
						borderRadius: 10,
						overflowX: 'auto',
						maxWidth: '100%',
					}}
				>
					<table style={{ width: '100%', minWidth: '1180px', borderCollapse: 'collapse' }}>
						<thead style={{ background: '#f7faf7' }}>
							<tr>
								{columns.map((column: any, index) => (
									<th
										key={column.id ?? column.accessorKey ?? index}
										style={{
											padding: '12px',
											textAlign: 'left',
											whiteSpace: 'nowrap',
											color: '#1f7a44',
											fontWeight: 600,
											borderBottom: '1px solid #e7eee8',
										}}
									>
										{column.header}
									</th>
								))}
								{enableRowActions && <th style={{ minWidth: actionColumnSize }} />}
							</tr>
						</thead>
						<tbody>
							{(data ?? []).map((row: any, rowIndex) => (
								<tr key={row.id ?? rowIndex} style={{ background: row.deleted_at ? '#fff1f2' : '#fff' }}>
									{columns.map((column: any, columnIndex) => (
										<td
											key={column.id ?? column.accessorKey ?? columnIndex}
											style={{
												padding: compact ? '6px 10px' : '10px 16px',
												height: compact ? 58 : 72,
												borderBottom: '1px solid #edf2ee',
												verticalAlign: 'middle',
												whiteSpace: 'nowrap',
											}}
										>
											{renderLeanCell(column, row)}
										</td>
									))}
									{enableRowActions && (
										<td style={{ padding: '10px 12px', borderBottom: '1px solid #edf2ee' }}>
											{renderLeanActions(row)}
										</td>
									)}
								</tr>
							))}
							{!loading && !(data ?? []).length && (
								<tr><td colSpan={columns.length + (enableRowActions ? 1 : 0)} style={{ padding: 24, textAlign: 'center' }}>No records</td></tr>
							)}
							{loading && (
								<tr><td colSpan={columns.length + (enableRowActions ? 1 : 0)} style={{ padding: 24, textAlign: 'center' }}>Loading...</td></tr>
							)}
						</tbody>
					</table>
				</div>
			) : (
			<MantineReactTable
				mantineProgressProps={{ color: color.CmsPrimary }}
				state={{
					isLoading: loading,
					...(lockActionColumn
						? {
								columnPinning: {
									right: ['mrt-row-actions'],
								},
						  }
						: {}),
				}}
				initialState={{
					showColumnFilters: false,
					showGlobalFilter: false,
					columnPinning: {
						right: ['mrt-row-actions'],
					},
					...(shouldEnableExpanding && autoExpandAll ? { expanded: true } : {}),
				}}
				columns={columns}
				data={data ?? []}
				mantinePaperProps={{
					shadow: '0',
					sx: {
						border: isBookStyle ? '1px solid #e3ece5' : '1px solid #e6ebe7',
						borderRadius: 10,
						overflow: lockActionColumn ? 'visible' : 'hidden',
						maxWidth: '100%',
					},
				}}
				mantineTableContainerProps={{
					sx: {
						isolation: 'isolate',
						overflowX: 'auto',
						WebkitOverflowScrolling: 'touch',
						maxWidth: '100%',
					},
				}}
				enableEditing={false}
				enableSorting={false}
				enableColumnActions={false}
				enableColumnPinning
				enableTopToolbar={false}
				enableBottomToolbar={false}
				positionActionsColumn="last"
				displayColumnDefOptions={{
					'mrt-row-actions': {
						size: actionColumnSize,
						mantineTableHeadCellProps: {
							sx: {
								...stickyActionStyles,
								background: actionColumnBackground,
							},
						},
						mantineTableBodyCellProps: {
							sx: {
								...stickyActionStyles,
								background: actionColumnBackground,
							},
						},
					},
				}}
				renderRowActions={({ row }: any) => {
					return (
						<Flex gap={8} align="center">
							<>
								{_renderCustomActions && _renderCustomActions?.(row)}
								{handleDetail && (
									<Stack sx={{ order: 10 }}>
										{disableActionTooltips ? detailActionButton(row) : (
											<CustomTooltip withinPortal withArrow label={detailActionLabel}>
												{detailActionButton(row)}
											</CustomTooltip>
										)}
									</Stack>
								)}
								{handleDetail && !hideMoreActions && (
									<Stack sx={{ order: 15 }}>
										<CustomTooltip withinPortal withArrow label="More actions">
											<button
												type="button"
												onClick={() => handleDetail(row)}
												className={getActionButtonClassName('muted')}
											>
												<IconDotsVertical size={16} />
											</button>
										</CustomTooltip>
									</Stack>
								)}
								{handleEdit && (
									<Stack sx={{ order: 20 }}>
										<CustomTooltip withinPortal withArrow label={editActionLabel}>
											<button
												type="button"
												className={getActionButtonClassName('default')}
												onClick={() => handleEdit(row)}
											>
												<IconEdit size={16} />
											</button>
										</CustomTooltip>
									</Stack>
								)}
								{handleDelete && (
									<Stack sx={{ order: 30 }}>
										{row?.original?.deleted_at ? (
											<button
												type="button"
												className={getActionButtonClassName('disabled')}
												disabled
											>
												<IconTrash size={16} />
											</button>
										) : (
											<CustomTooltip withinPortal withArrow label="Delete">
												<button
													type="button"
													className={getActionButtonClassName('danger')}
													onClick={() => handleDelete(row)}
												>
													<IconTrash size={16} />
												</button>
											</CustomTooltip>
										)}
									</Stack>
								)}
							</>
						</Flex>
					)
				}}
				mantineLoadingOverlayProps={{
					sx: {
						svg: {
							stroke: color.CmsPrimary,
						},
					},
				}}
				rowCount={total}
				mantineColumnActionsButtonProps={{
					sx: {
						background: 'red',
					},
				}}
				// renderTopToolbar={({}) => <></>}
				// renderBottomToolbar={({}) => <div>a</div>}
				// onPaginationChange={setPagination}
				enablePagination={false}
				// UI for Table
				mantineTableHeadCellProps={{
					sx: {
						'& .mantine-TableHeadCell-Content': {
							alignItems: 'center',
							justifyContent: 'flex-start',
							width: '100%',
						},
						'& .mantine-TableHeadCell-Content-Wrapper': {
							width: '100%',
							textAlign: 'left',
						},
						background: isBookStyle ? '#f5faf6' : '#f7faf7',
						borderBottom: isBookStyle
							? '1px solid #e2ebe4'
							: '1px solid #e7eee8',
						paddingTop: compact
							? '8px !important'
							: isBookStyle
								? '10px !important'
								: '12px !important',
						paddingBottom: compact
							? '8px !important'
							: isBookStyle
								? '10px !important'
								: '12px !important',
					},
				}}
				// UI Cell
				mantineTableBodyCellProps={({ row }: any) => {
					return {
						sx: {
							background: row?.original?.deleted_at ? '#fff1f2' : 'inherit',
							height: compact ? 58 : isBookStyle ? 66 : 72,
							padding: compact
								? '6px 8px 6px 10px !important'
								: isBookStyle
									? '8px 12px 8px 16px !important'
									: '10px 12px 10px 16px !important',
							whiteSpace: 'normal',
							boxSizing: 'border-box',
							borderBottom: '1px solid #edf2ee',
							verticalAlign: 'middle',
						},
					}
				}}
				mantineTableBodyRowProps={({ row }: any) => {
					return {
						sx: {
							backgroundColor: row?.original?.deleted_at ? '#fff1f2' : '#ffffff',
							transition: 'background-color 120ms ease',
							'&:hover td': {
								backgroundColor: row?.original?.deleted_at
									? '#ffe4e6'
									: '#f8fafc',
							},
						},
					}
				}}
				// UI head
				mantineTableHeadRowProps={() => {
					return {
						sx: {
							'.mantine-TableHeadCell-Content-Wrapper': {
								color: isBookStyle ? '#21693f' : '#1f7a44',
								fontWeight: 600,
								whiteSpace: 'nowrap',
								textAlign: 'left',
								width: '100%',
							},
							th: {
								verticalAlign: 'middle',
								paddingLeft: compact
									? '8px !important'
									: '12px !important',
								paddingRight: compact
									? '8px !important'
									: '12px !important',
							},
						},
					}
				}}
				//UI Body
				// mantineTableBodyProps={() => {
				// 	return {
				// 		sx: { height: 401 },
				// 	}
				// }}
				enableRowNumbers={false}
				enableRowActions={enableRowActions}
				enableExpanding={shouldEnableExpanding}
				{...(shouldEnableExpanding
					? {
						mantineDetailPanelProps: {
							sx: {
								width: '100%',
								maxWidth: '100%',
								padding: 0,
								backgroundColor: '#f8fbf9',
								overflow: 'visible',
							},
						},
						mantineExpandButtonProps: ({ row }: any) => {
							const isActive = rowHasSubTable(row)

							return {
								disabled: !isActive,
								sx: {
									color: isActive ? '#ffffff' : '#94a3b8',
									backgroundColor: isActive ? color.CmsPrimary : '#e2e8f0',
									borderRadius: 8,
									border: `1px solid ${
										isActive ? color.CmsPrimary : '#cbd5e1'
									}`,
									'&:hover': {
										backgroundColor: isActive
											? color.CmsPrimary
											: '#e2e8f0',
									},
									'&:disabled': {
										opacity: 0.65,
										cursor: 'not-allowed',
									},
								},
							}
						},
						renderDetailPanel,
					}
					: {})}

			/>
			)}
			{/* <Space h="sm" /> */}
			{!hidePagination && total > 0 && (
				<Flex wrap="wrap" gap={16}>
					<span style={{ color: '#6b7280', fontSize: 14 }}>
						Showing {from} to {to} from {total} entries
					</span>
					<Pagination
						sx={{
							flex: 1,
							minWidth: 300,
							button: {
								color: color.CmsPrimary,
								'&[data-active]': {
									color: 'white',
									background: color.CmsPrimary,
									':hover': {
										color: 'white',
										background: `${color.CmsPrimary} !important`,
									},
								},
							},
						}}
						position="right"
						total={Math.ceil(total / pagination.pageSize) || 1}
						onChange={(value) => {
							handleChangePage(value)
						}}
						value={pagination.pageIndex}
						disabled={loading}
					/>
				</Flex>
			)}
		</Stack>
	)
}

const MemoizedTableComponent = React.memo(TableComponent) as typeof TableComponent

export default MemoizedTableComponent
